import './code-highlight-block.css';
// import './katex.css';

import type { Options } from 'react-markdown';

import Link from '@mui/material/Link';
import { useEffect, useMemo, useRef } from 'react';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import { isExternalLink } from '../../utils/route-utils';
import { RouterLink } from '../router-link';

import { Image } from '../image';
import { markdownClasses } from './classes';
import { htmlToMarkdown, isMarkdownContent } from './html-to-markdown';
import { StyledRoot } from './styles';

import { styled } from '@mui/material';
import MermaidRenderer from './mermaid';
import type { MarkdownProps } from './types';

// ----------------------------------------------------------------------

const preprocessMarkdown = (markdownText: string) => {
  // Replace \[ with $$ and \] with $$ to ensure compatibility
  const processedText = markdownText
    .replace(/\\\[/g, '$$$') // Replace all occurrences of \[ with $$
    .replace(/\\\]/g, '$$$') // Replace all occurrences of \] with $$
    .replace(/\\\(/g, '$$$') // Replace all occurrences of \( with $$
    .replace(/\\\)/g, '$$$'); // Replace all occurrences of \) with $$

  return processedText;
};

export function MarkdownComponent({ children, sx, ...other }: MarkdownProps) {
  const content = useMemo(() => {
    if (isMarkdownContent(children)) {
      return preprocessMarkdown(children);
    }
    return htmlToMarkdown(`${children}`.trim());
  }, [children]);

  return (
    <StyledRoot
      children={content}
      components={components as Options['components']}
      rehypePlugins={rehypePlugins as Options['rehypePlugins']}
      remarkPlugins={remarkPlugins as Options['remarkPlugins']}
      /* base64-encoded images
       * https://github.com/remarkjs/react-markdown/issues/774
       * urlTransform={(value: string) => value}
       */
      className={markdownClasses.root}
      sx={sx}
      {...other}
    />
  );
}

// ----------------------------------------------------------------------

type ComponentTag = {
  [key: string]: any;
};
const containsMermaidSyntax = (code: string) => {
  return /\b(graph\s+(TD|LR|RL|BT)|sequenceDiagram|flowchart|pie|gantt|classDiagram|stateDiagram(-v2)?|erDiagram|journey|requirementDiagram|mindmap|entityRelationship|gitGraph)\b|%%|-->|==>|:::|click\b/.test(
    code
  );
};

const rehypePlugins = [rehypeRaw, rehypeHighlight, rehypeKatex];
const remarkPlugins = [remarkGfm, remarkMath];
const components = {
  img: ({ node, ...other }: ComponentTag) => <Image ratio="16/9" className={markdownClasses.content.image} sx={{ borderRadius: 2 }} {...other} />,
  a: ({ href, children, node, ...other }: ComponentTag) => {
    const linkProps = isExternalLink(href) ? { target: '_blank', rel: 'noopener' } : { component: RouterLink };

    return (
      <Link {...linkProps} href={href} className={markdownClasses.content.link} {...other}>
        {children}
      </Link>
    );
  },
  pre: ({ children }: ComponentTag) => (
    <div className={markdownClasses.content.codeBlock}>
      <pre>{children}</pre>
    </div>
  ),
  code({ className, children, node, ...other }: ComponentTag) {
    const language = /language-(\w+)/.exec(className || '');
    const codeContent = String(children).trim();
    if ((language && language[1] === 'mermaid') || containsMermaidSyntax(codeContent)) {
      return <MermaidRenderer chart={String(children)} />;
    }

    return language ? (
      <code {...other} className={className}>
        {children}
      </code>
    ) : (
      <code {...other} className={markdownClasses.content.codeInline}>
        {children}
      </code>
    );
  },
  // math: ({ value }: { value: string }) => <BlockMath>{value}</BlockMath>,
  // inlineMath: ({ value }: { value: string }) => <InlineMath>{value}</InlineMath>,
};

export function Markdown(props: Options) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            node.classList.add('fade-in');
          }
        });
      }
    });

    const container = containerRef.current;
    if (container) {
      observer.observe(container, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <FadeContainer ref={containerRef}>
      <MarkdownComponent {...props} />
    </FadeContainer>
  );
}

const FadeContainer = styled('div')(() => ({
  '& .fade-in': {
    opacity: 0,
    animation: 'muiFadeIn 1s ease forwards',
  },
  '@keyframes muiFadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
}));
